
import Vue from "vue";
import { Component } from "vue-property-decorator";
import {
  addPageInWP,
  addOrUpdatePageInWP
} from "@/lib/singularity/wp-page-manager/pages";
import { generatePageInTheBackground } from "@/futurelayer/pages/background-page-generation";
import { addAiJobAndGetValueReturnedByCallback } from "@/futurelayer/ai-job-manager/ai-jobs";
import { saveToWordPressWithErrorsLoggedToUiStore } from "@/lib/wordpress/save";
import { replaceDocumentWithAnother } from "@/lib/wordpress/load";
import { singularityStore } from "@/store/store";
import { addHeaderAndFooterNodesToTree } from "@/futurelayer/headers-footers/headers-footers";
import { getBlankTree } from "@/lib/tree/tree";
import { fetchHeaderAndFooterBlockIdsAndSyncToStore } from "@/futurelayer/headers-footers/api";
import Icon from "@/components/UI/Icon.vue";
import IconButton from "@/components/UI/IconButton.vue";
import NiceButton from "@/components/UI/NiceButton.vue";
import { fireConfetti } from "@/subapps/onboarding/lib/confetti";
import SingularityOption from "../components/SingularityOption.vue";
import { logEvent } from "@/util/tracking";

type WizardStep = "choose-type" | "page-details" | "creating" | "success";
type PageType = "blank" | "ai";

@Component({
  components: {
    Icon,
    IconButton,
    NiceButton,
    SingularityOption
  }
})
export default class WPPageManagerAddPageWizard extends Vue {
  currentStep: WizardStep = "choose-type";
  selectedType: PageType | null = null;
  pageTitle = "";
  pageDescription = "";
  createdPageId: number | null = null;
  openingInProgress = false;

  selectPageType(type: PageType) {
    this.selectedType = type;
    this.currentStep = "page-details";
    this.$nextTick(() => {
      (this.$refs.pageTitleInput as HTMLInputElement)?.focus();
    });
    logEvent("FutureLayer WP Page Manager: Page Type Selected", { type });
  }

  goBack() {
    if (this.currentStep === "page-details") {
      this.currentStep = "choose-type";
      this.selectedType = null;
    }
  }

  async createPage() {
    if (!this.pageTitle.trim()) return;

    this.currentStep = "creating";

    try {
      // Ensure header and footer global blocks are loaded and synced to store
      await fetchHeaderAndFooterBlockIdsAndSyncToStore();

      let result;

      if (this.selectedType === "ai") {
        const initialPage = await addPageInWP(this.pageTitle, {}, "page");
        if (!initialPage) return;

        result = await addAiJobAndGetValueReturnedByCallback(
          async abortController => {
            return await generatePageInTheBackground(
              this.pageTitle,
              initialPage.id,
              abortController,
              this.pageDescription || undefined
            );
          },
          {
            jobType: "background_page_generation",
            pageId: initialPage.id,
            pageTitle: this.pageTitle,
            description: `Generating page: ${this.pageTitle}`,
            priority: 'normal'
          }
        );
        logEvent("FutureLayer WP Page Manager: AI Page Generation Started", {
          title: this.pageTitle,
          description: this.pageDescription || null
        });
      } else {
        const blankTree = getBlankTree();
        const treeWithHeadersAndFooters =
          addHeaderAndFooterNodesToTree(blankTree);

        result = await addOrUpdatePageInWP({
          title: this.pageTitle,
          postType: "page",
          tree: treeWithHeadersAndFooters,
          singularityMeta: {}
        });
        logEvent("FutureLayer WP Page Manager: Blank Page Creation Started", {
          title: this.pageTitle
        });
      }

      if (!result) return;

      this.createdPageId = result.id;
      this.currentStep = "success";
      fireConfetti();
    } catch (error) {
      console.error("Failed to create page:", error);
    }
  }

  async editPage() {
    if (this.createdPageId) {
      this.openingInProgress = true;
      await saveToWordPressWithErrorsLoggedToUiStore();
      await replaceDocumentWithAnother(this.createdPageId);
      singularityStore.setSingularityLeftPanel("page_structure");
      this.openingInProgress = false;
    }
  }

  backToPages() {
    this.$emit("close");
  }
}
